import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ContactCategory {
  id: string;
  name: string;
  description?: string;
  department_email?: string;
  auto_response_template?: string;
  priority_level: 'low' | 'medium' | 'high' | 'urgent';
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const useContactCategories = () => {
  const [categories, setCategories] = useState<ContactCategory[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('contact_categories')
        .select('*')
        .order('name');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching contact categories:', error);
      toast.error('Failed to load contact categories');
    } finally {
      setLoading(false);
    }
  };

  const createCategory = async (categoryData: Omit<ContactCategory, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('contact_categories')
        .insert([categoryData])
        .select()
        .single();

      if (error) throw error;
      
      setCategories(prev => [...prev, data]);
      toast.success('Contact category created successfully');
      return data;
    } catch (error) {
      console.error('Error creating contact category:', error);
      toast.error('Failed to create contact category');
      throw error;
    }
  };

  const updateCategory = async (id: string, updates: Partial<ContactCategory>) => {
    try {
      const { data, error } = await supabase
        .from('contact_categories')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      setCategories(prev => 
        prev.map(cat => cat.id === id ? { ...cat, ...data } : cat)
      );
      toast.success('Contact category updated successfully');
      return data;
    } catch (error) {
      console.error('Error updating contact category:', error);
      toast.error('Failed to update contact category');
      throw error;
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      const { error } = await supabase
        .from('contact_categories')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setCategories(prev => prev.filter(cat => cat.id !== id));
      toast.success('Contact category deleted successfully');
    } catch (error) {
      console.error('Error deleting contact category:', error);
      toast.error('Failed to delete contact category');
      throw error;
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  return {
    categories,
    loading,
    createCategory,
    updateCategory,
    deleteCategory,
    refetch: fetchCategories
  };
};